import React, { useEffect, useMemo } from 'react';
import { Card, Form, Input, Select, Space, Typography, message } from 'antd';
import type { QaqcPipeWorkarea } from '../../../../types';
import { insertData, updateData } from '../../../../services/api';
import { useGetQaqcPipeWorkarea } from '../../../../hooks/genericHooks';
import { useNavigate } from 'react-router-dom';
import Button from '../../../../components/common/Button';

export type QaqcPipeWorkareaMode = 'create' | 'edit';

interface QaqcPipeWorkareaCreateProps {
  mode?: QaqcPipeWorkareaMode;
  initialValues?: Partial<QaqcPipeWorkarea> | null;
  onSaved?: (saved: QaqcPipeWorkarea | any) => void;
  onCancel?: () => void;
  onBack?: () => void;
  viewOnly?: boolean;
}

const { Title } = Typography;

const QaqcPipeWorkareaCreate: React.FC<QaqcPipeWorkareaCreateProps> = ({
  mode = 'create',
  initialValues,
  onSaved,
  onCancel,
  onBack,
  viewOnly,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm<QaqcPipeWorkarea>();
  const isEdit = mode === 'edit';
  const isViewOnly = !!viewOnly;

  // Load existing to suggest section tags
  const { data: listResp } = useGetQaqcPipeWorkarea();
  const sectionOptions = useMemo(() => {
    const set = new Set<string>();
    const rows: QaqcPipeWorkarea[] = (listResp as any)?.data || [];
    (rows || []).forEach(r => (r.section_list || []).forEach(s => s && set.add(s)));
    return Array.from(set).sort().map(s => ({ value: s, label: s }));
  }, [listResp]);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        name: initialValues.name,
        section_list: initialValues.section_list || [],
      } as any);
    } else {
      form.resetFields();
    }
  }, [initialValues]);

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: Partial<QaqcPipeWorkarea> = {
        name: values.name?.trim(),
        section_list: values.section_list || [],
      };

      if (isEdit && initialValues?.id) {
        await updateData('qaqc_pipe_workarea', initialValues.id, payload, 'id');
        message.success('Work area updated');
        try { window.dispatchEvent(new Event('qaqc_pipe_workarea:changed')); } catch {}
        if (onSaved) {
          onSaved({ id: initialValues.id, ...payload });
        } else {
          navigate('/qaqc/piping/workareas');
          setTimeout(() => {
            try { window.dispatchEvent(new Event('qaqc_pipe_workarea:changed')); } catch {}
          }, 100);
        }
      } else {
        const resp = await insertData('qaqc_pipe_workarea', payload);
        message.success('Work area created');
        try { window.dispatchEvent(new Event('qaqc_pipe_workarea:changed')); } catch {}
        const newRec = { id: resp?.ids?.[0], ...payload } as any;
        if (onSaved) {
          onSaved(newRec);
          form.resetFields();
        } else {
          navigate('/qaqc/piping/workareas');
          setTimeout(() => {
            try { window.dispatchEvent(new Event('qaqc_pipe_workarea:changed')); } catch {}
          }, 100);
        }
      }
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.error || 'Save failed');
    }
  };

  return (
    <Card
      bordered
      size="small"
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 18 }}>
            {(isEdit || isViewOnly) && onBack && (
              <a onClick={onBack} style={{ marginRight: 16, fontSize: 16 }}>&larr; Back</a>
            )}
            {isViewOnly ? 'View Work Area' : (isEdit ? 'Edit Work Area' : 'Create Work Area')}
          </span>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Space wrap style={{ gap: 12 }}>
            <Form.Item
              label="Area Name"
              name="name"
              rules={[{ required: true, message: 'Enter area name' }, { max: 100 }]}
              style={{ marginBottom: 0 }}
            >
              <Input placeholder="Work area name" maxLength={100} disabled={isViewOnly} style={{ width: 320 }} />
            </Form.Item>

            <Form.Item
              label="Sections"
              name="section_list"
              tooltip="Add or choose one or more sections"
              style={{ marginBottom: 0 }}
            >
              <Select
                mode="tags"
                allowClear
                options={sectionOptions}
                placeholder="Type and press Enter to add"
                disabled={isViewOnly}
                style={{ minWidth: 420, maxWidth: 800 }}
              />
            </Form.Item>
          </Space>

          {!isViewOnly && (
            <Space>
              <Button action={isEdit ? 'edit' : 'add'} type="primary" onClick={onSubmit}>{isEdit ? 'Update' : 'Create'}</Button>
              {onCancel && <Button onClick={onCancel}>Cancel</Button>}
            </Space>
          )}
        </Space>
      </Form>
    </Card>
  );
};

export default QaqcPipeWorkareaCreate;
